import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Languages, RefreshCw, Play, Loader2, CheckCircle2, AlertCircle,
  ScrollText, LifeBuoy, Sparkles, BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { API_URL as API } from '@/utils/apiConfig';
import AdminLayout from './AdminLayout';

const COLLECTION_ICONS = {
  legal: ScrollText,
  faq: LifeBuoy,
  dyk: Sparkles,
  reading_plans: BookOpen,
};

const LOCALE_LABEL = {
  es: 'Spanish',
  fr: 'French',
  pt: 'Portuguese',
};

function coverageColor(pct) {
  if (pct >= 95) return '#22c55e';
  if (pct >= 60) return '#eab308';
  if (pct > 0) return '#f97316';
  return '#ef4444';
}

function CoverageBar({ pct, color, testid }) {
  return (
    <div
      className="h-2 w-full rounded-full overflow-hidden bg-black/10"
      data-testid={testid}
    >
      <div
        className="h-full transition-all"
        style={{ width: `${Math.min(pct, 100)}%`, background: color }}
      />
    </div>
  );
}

function CollectionCard({ collection, colors, onBackfill, runningJob }) {
  const Icon = COLLECTION_ICONS[collection.collection] || Languages;
  const overallPct = collection.total
    ? Math.round(
        (collection.locales.reduce((acc, l) => acc + l.translated, 0) /
          (collection.total * collection.locales.length)) * 100
      )
    : 100;
  const isRunning = runningJob && runningJob.collection === collection.collection && runningJob.status !== 'done' && runningJob.status !== 'failed';

  return (
    <div
      className="rounded-xl p-5 border"
      style={{ background: colors.card, borderColor: colors.border }}
      data-testid={`i18n-collection-${collection.collection}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="p-2 rounded-lg"
            style={{ background: colors.cardMuted || 'rgba(0,0,0,0.05)' }}
          >
            <Icon className="h-5 w-5" style={{ color: colors.accent }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: colors.text }}>
              {collection.label}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
              {collection.total} source row{collection.total === 1 ? '' : 's'} · {overallPct}% covered
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={isRunning}
          onClick={() => onBackfill(collection.collection, { force: false })}
          data-testid={`i18n-backfill-${collection.collection}`}
        >
          {isRunning ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Running…</>
          ) : (
            <><Play className="h-3.5 w-3.5 mr-1.5" /> Backfill missing</>
          )}
        </Button>
      </div>

      <div className="space-y-2.5">
        {collection.locales.map((l) => (
          <div key={l.locale} data-testid={`i18n-locale-row-${collection.collection}-${l.locale}`}>
            <div className="flex items-center justify-between text-xs mb-1" style={{ color: colors.textMuted }}>
              <span className="font-medium" style={{ color: colors.text }}>
                {LOCALE_LABEL[l.locale] || l.locale} · <span style={{ color: colors.textMuted }}>{l.locale}</span>
              </span>
              <span>
                {l.translated}/{collection.total}
                <span className="ml-2 font-semibold" style={{ color: coverageColor(l.coverage_pct) }}>
                  {l.coverage_pct}%
                </span>
              </span>
            </div>
            <CoverageBar
              pct={l.coverage_pct}
              color={coverageColor(l.coverage_pct)}
              testid={`i18n-bar-${collection.collection}-${l.locale}`}
            />
            {l.missing && l.missing.length > 0 && (
              <details className="mt-1.5 text-[11px]" style={{ color: colors.textMuted }}>
                <summary className="cursor-pointer hover:underline">
                  {l.missing.length} missing
                </summary>
                <div className="mt-1 font-mono text-[10px] leading-relaxed break-all opacity-80">
                  {l.missing.slice(0, 20).join(', ')}
                  {l.missing.length > 20 && ` … +${l.missing.length - 20} more`}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>

      {isRunning && runningJob && (
        <div
          className="mt-4 p-2.5 rounded-lg text-xs flex items-center gap-2"
          style={{ background: 'rgba(59,130,246,0.12)', color: colors.text }}
          data-testid={`i18n-job-${collection.collection}`}
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>
            Translating {runningJob.translated}/{runningJob.total || '?'}…
          </span>
        </div>
      )}
    </div>
  );
}

export default function AdminI18nStatusPage() {
  const { colors } = useAdminTheme();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningJobs, setRunningJobs] = useState({}); // collection → job
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/i18n/status`, { withCredentials: true });
      setStatus(res.data);
      // Reconcile running jobs from server response so a page refresh
      // during a backfill picks the progress back up.
      const active = {};
      (res.data.jobs || []).forEach((j) => {
        if (j.status === 'running' || j.status === 'queued') {
          active[j.collection] = j;
        }
      });
      setRunningJobs((prev) => ({ ...prev, ...active }));
    } catch (e) {
      toast.error('Failed to load i18n status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Poll every 4s while any job is running.
  useEffect(() => {
    const anyRunning = Object.values(runningJobs).some(
      (j) => j && (j.status === 'running' || j.status === 'queued')
    );
    if (!anyRunning) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      // Refresh both the status and any active jobs.
      await fetchStatus();
      const updated = {};
      for (const [collection, job] of Object.entries(runningJobs)) {
        if (!job || job.status === 'done' || job.status === 'failed') {
          updated[collection] = job;
          continue;
        }
        try {
          const r = await axios.get(`${API}/admin/i18n/jobs/${job.job_id}`, { withCredentials: true });
          updated[collection] = r.data;
          if (r.data.status === 'done') {
            toast.success(`${collection}: backfill complete (${r.data.translated}/${r.data.total})`);
          } else if (r.data.status === 'failed') {
            toast.error(`${collection}: backfill failed — ${r.data.error || 'unknown error'}`);
          }
        } catch {
          updated[collection] = job;
        }
      }
      setRunningJobs(updated);
    }, 4000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [runningJobs, fetchStatus]);

  const startBackfill = useCallback(async (collection, { force = false } = {}) => {
    try {
      const res = await axios.post(
        `${API}/admin/i18n/backfill/${collection}`,
        { force },
        { withCredentials: true }
      );
      toast.info(`${collection}: backfill queued`);
      setRunningJobs((prev) => ({
        ...prev,
        [collection]: { ...res.data, translated: 0, total: 0 },
      }));
    } catch (e) {
      toast.error(`Failed to start backfill: ${e?.response?.data?.detail || e.message}`);
    }
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-5xl" data-testid="admin-i18n-status-page">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: colors.text }}>
              <Languages className="h-5 w-5" style={{ color: colors.accent }} />
              Content Translation Coverage
            </h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>
              Track which DB-driven content has been translated into es / fr / pt. Run a
              backfill to catch any rows that were added or edited before auto-translate
              was enabled.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchStatus}
            disabled={loading}
            data-testid="i18n-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loading && !status ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} />
          </div>
        ) : status ? (
          <div className="grid gap-4 md:grid-cols-2">
            {status.collections.map((c) => (
              <CollectionCard
                key={c.collection}
                collection={c}
                colors={colors}
                onBackfill={startBackfill}
                runningJob={runningJobs[c.collection]}
              />
            ))}
          </div>
        ) : (
          <div className="text-sm py-8 text-center" style={{ color: colors.textMuted }}>
            <AlertCircle className="h-6 w-6 mx-auto mb-2" />
            No coverage data available.
          </div>
        )}

        {status && status.jobs && status.jobs.length > 0 && (
          <div className="mt-8">
            <h2 className="text-sm font-semibold mb-3" style={{ color: colors.text }}>
              Recent jobs
            </h2>
            <div
              className="rounded-lg border overflow-hidden"
              style={{ borderColor: colors.border, background: colors.card }}
              data-testid="i18n-recent-jobs"
            >
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left" style={{ color: colors.textMuted, background: colors.cardMuted || 'rgba(0,0,0,0.03)' }}>
                    <th className="px-3 py-2">Collection</th>
                    <th className="px-3 py-2">Kind</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Progress</th>
                    <th className="px-3 py-2">Started</th>
                  </tr>
                </thead>
                <tbody style={{ color: colors.text }}>
                  {status.jobs.map((j) => (
                    <tr key={j.job_id} className="border-t" style={{ borderColor: colors.border }}>
                      <td className="px-3 py-2 font-medium">{j.collection}</td>
                      <td className="px-3 py-2 text-[11px] uppercase tracking-wide" style={{ color: colors.textMuted }}>{j.kind}</td>
                      <td className="px-3 py-2">
                        {j.status === 'done' && <CheckCircle2 className="h-3.5 w-3.5 inline text-green-500 mr-1" />}
                        {j.status === 'failed' && <AlertCircle className="h-3.5 w-3.5 inline text-red-500 mr-1" />}
                        {(j.status === 'running' || j.status === 'queued') && <Loader2 className="h-3.5 w-3.5 inline animate-spin mr-1" />}
                        {j.status}
                      </td>
                      <td className="px-3 py-2">{j.translated}/{j.total || '—'}</td>
                      <td className="px-3 py-2 text-[11px]" style={{ color: colors.textMuted }}>
                        {j.started_at ? new Date(j.started_at).toLocaleString() : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
